<template>
  <HgCardFlat
    :class="[
      $style.container,
      {
        [$style.highlightedHg]: props.data.hasNotEngaged && !isIs24,
        [$style.highlightedIs24]: props.data.hasNotEngaged && isIs24,
      },
    ]"
  >
    <div :class="$style.mapWrapper">
      <StaticMap
        :lon="data.geo.lon"
        :lat="data.geo.lat"
        :size="mapSize"
        :hasBorderRadius="false"
        @click="handlePropertyClick"
      />
    </div>
    <HgBadge
      v-if="props.data.offerType !== OfferType.Other"
      :text="offerType"
      theme="info"
      :class="$style.offerTypebadge"
      :small="true"
    />
    <HgBadge
      v-if="props.data.hasNotEngaged"
      :text="t('overview.newlyAdded.badge')"
      :theme="isIs24 ? 'skylineblue' : 'blue'"
      :class="$style.newlyAddedBadge"
      :small="true"
    />
    <div :class="$style.contentContainer">
      <div :class="$style.bodyWrapper" @click="handlePropertyClick">
        <div>
          <h5 :class="$style.title">{{ data.street }}</h5>
          <p :class="$style.address">
            <HgIcon icon="Location" /><span>{{ data.shortAddress }}</span>
          </p>
        </div>
        <p :class="$style.normalText">
          <strong>{{ t(`common.${data.propertyType}`) }}</strong> |
          {{ t('common.room.capitalized', data.room) }} | {{ data.livingSpace }} m²
        </p>
        <div :class="$style.priceWrapper">
          <p :class="$style.normalTextBold">~ CHF {{ data.medianPrice }}</p>
          <HgTooltip id="property-tile-tooltip" :class="$style.tooltip">
            <div :class="$style.tooltipWrapper">
              <p :class="$style.normalTextBold">{{ t('tooltip.marketPrice.title') }}&nbsp;</p>
              <p :class="$style.normalText">
                {{
                  t('tooltip.marketPrice.description', {
                    priceFrom: data.priceRange.from,
                    priceTo: data.priceRange.to,
                  })
                }}
              </p>
            </div>
          </HgTooltip>
          <div v-if="data.appraisalChangeRate !== undefined" :class="$style.priceChangeWrapper">
            <HgBadge
              :class="{ [$style.isHg]: !isIs24 }"
              :theme="appraisalChangeRateClass(data.appraisalChangeRate)"
              :small="true"
              >{{ formatPercentage(data.appraisalChangeRate) }}</HgBadge
            >
            <span>{{ t('common.appraisalChangeRate.label') }}</span>
          </div>
        </div>
      </div>

      <div :class="$style.footerWrapper">
        <HgLink tag="button" icon="Edit" @click="handleEdit">
          {{ t('button.edit') }}
        </HgLink>
        <HgLink tag="button" icon="Delete" @click="handleRemove">
          {{ t('button.remove') }}
        </HgLink>
      </div>
    </div>
  </HgCardFlat>
</template>

<script setup lang="ts">
import { PropertyTileData } from '@/components/property-overview-page/MyProperties/adaptData';
import { formatPercentage as formatPercentageShared } from '@/helpers/Number';
import { isPlatformIs24 } from '@/helpers/Platform/Platform';
import { OfferType } from '@/models/valuationFunnel';
import { trackingClickEvent } from '@/tracking/tracking';
import { TRACKING_ACTION, TRACKING_LABEL } from '@/tracking/types';
import {
  HgBadge,
  HgCardFlat,
  HgIcon,
  HgLink,
  HgSupportedLanguage,
  HgTooltip,
} from '@smg-real-estate/domus';
import { computed, defineAsyncComponent } from 'vue';
import { useI18n } from 'vue-i18n';

const StaticMap = defineAsyncComponent(
  () => import('@/components/common/GoogleMaps/StaticMap.vue'),
);
export interface Props {
  data: PropertyTileData;
  mapSize?: string;
}
const props = withDefaults(defineProps<Props>(), {
  mapSize: '385x153',
});

const emit = defineEmits<{
  (event: 'onRemove', property: PropertyTileData): void;
  (event: 'onPropertyClick' | 'onEdit', id: string): void;
}>();

const { t, locale } = useI18n();

const offerType = computed<string>(() => {
  const initialOfferType = `offerType.${OfferType[props.data.offerType].toLowerCase()}`;

  return t(initialOfferType.replace('selling', 'own'));
});

const isIs24 = isPlatformIs24();

// Methods
function formatPercentage(value: number): string {
  return formatPercentageShared(value, locale.value as HgSupportedLanguage, { showSign: true });
}

function appraisalChangeRateClass(value: number): string {
  if (isIs24) {
    if (value === 0) {
      return 'onhold';
    } else if (value > 0) {
      return 'primarylight';
    } else {
      return 'critical';
    }
  } else {
    if (value === 0) {
      return 'onhold';
    } else if (value > 0) {
      return 'greenlight';
    } else {
      return 'warning';
    }
  }
}

// Events
const handleRemove = () => {
  trackingClickEvent(
    TRACKING_ACTION.DELETE_PROPERTY_TRY,
    TRACKING_LABEL.PROPERTY_VALUATION_FORM,
    true,
    {
      h_lead_id_066: props.data.id,
    },
  );
  emit('onRemove', props.data);
};

const handleEdit = () => {
  trackingClickEvent(TRACKING_ACTION.EDIT_PROPERTY, TRACKING_LABEL.PROPERTY_VALUATION_FORM, true, {
    h_lead_id_066: props.data.id,
  });
  emit('onEdit', props.data.id);
};

const handlePropertyClick = (event: MouseEvent) => {
  // As HgTooltip doesn't have stopPropagation, we need to filter out the tooltip click by checking the closest element
  if (event.target instanceof Element && event.target.closest('#property-tile-tooltip')) {
    return;
  }

  emit('onPropertyClick', props.data.id);
};
</script>

<style module lang="scss">
@use '@/assets/variables' as *;

$tooltip-width: 180px;
$map-height: 153px;

.container[class*='HgCardFlat_cardFlat'] {
  position: relative;
  border-color: $color-grey-60;
  img {
    cursor: pointer;
  }
}

.highlightedHg[class*='HgCardFlat_cardFlat'] {
  border-width: 2px;
  border-color: $color-blue-60;
}

.highlightedIs24[class*='HgCardFlat_cardFlat'] {
  border-width: 2px;
  border-color: $color-azul-110;
}

.mapWrapper {
  min-height: $map-height;
}

.contentContainer {
  padding: $space-75;
}

.bodyWrapper {
  display: flex;
  flex-direction: column;
  gap: $space-50;
  padding-bottom: $space-75;
  cursor: pointer;
}

.offerTypebadge {
  position: absolute;
  top: $space-75;
  left: $space-75;
}

.newlyAddedBadge {
  position: absolute;
  top: $space-75;
  right: $space-75;
}

.title {
  @include heading-5;
  @include vertical-margin;
  @include truncated-text;
}
.address {
  display: flex;
  align-items: center;
  @include body-3;
  @include vertical-margin;

  > svg {
    flex-shrink: 0;
  }

  > span {
    @include truncated-text;
  }
}

.normalText {
  @include body-3;
  @include vertical-margin;
}

.normalTextBold {
  @include body-3-bold;
  @include vertical-margin;
}

.priceWrapper {
  display: flex;
  gap: $space-25;
}

.footerWrapper {
  display: flex;
  justify-content: space-between;
  padding-top: $space-75;
  border-top: 1px solid $color-grey-60;

  button {
    &:hover {
      text-decoration: none;
    }
  }
}

.tooltip [class*='HgTooltip_tippyHeader'] {
  svg {
    width: $space-75;
    height: $space-75;
  }
}

.tooltipWrapper {
  @include grid-media-query(sm) {
    width: $tooltip-width;
  }
}

.priceChangeWrapper {
  font-size: var(--typo-body-3-font-size);
  margin-left: $space-100;

  & span {
    margin-right: $space-25;
    font-size: inherit;
  }

  & .isHg {
    &[class*='_warning_'] {
      color: var(--message-color-danger-icon);
    }
    &[class*='greenlight'] {
      color: var(--message-color-success-icon);
    }
  }
}
</style>
